import { Modal } from '@/components/Modal';
import { useToast } from '@/components/Toast';
import {
  AlertCircle,
  BookOpen,
  Calendar,
  CalendarDays,
  Camera,
  CheckCircle2,
  Link as LinkIcon,
  Loader2,
  Lock,
  MessageCircle,
  Receipt,
  ScanBarcode,
  Star,
  Zap,
} from 'lucide-react';
/**
 * /settings/integrations — integration catalogue tile grid.
 *
 * Tiles are grouped by category (Accounting / Calendar / External services /
 * Communications). Each tile shows a branded icon, provider name + tagline,
 * a status pill, and a context-aware action button:
 *   - Not connected + available + tier allowed → "Connect" button.
 *   - Not connected + NOT available            → "Coming soon" pill (operator
 *       hasn't set the platform OAuth client ID/secret yet).
 *   - Not connected + tier locked              → "Upgrade" link.
 *   - Connected                                → "Settings" + "Disconnect" buttons.
 *   - Status expired/revoked                   → "Reconnect" button.
 *
 * On return from the OAuth callback, the browser lands here with:
 *   ?connected=<provider>  → success toast + tile refreshes.
 *   ?error=<provider>&reason=<reason> → error banner with retry CTA.
 *
 * Disconnect confirmation uses an inline modal (no third-party dialog dep).
 * Toast uses the shared useToast hook from components/Toast.tsx.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface IntegrationRow {
  provider: string;
  name: string;
  description: string;
  tier: 'all' | 'pro_or_higher' | 'enterprise_only';
  credentialShape:
    | 'oauth'
    | 'api_key_per_provider'
    | 'membership_signed_token'
    | 'no_credentials'
    | 'external';
  isOauth: boolean;
  externalSettingsPath: string | null;
  tierAllowed: boolean;
  available: boolean;
  status: 'connected' | 'expired' | 'revoked' | null;
  connected: boolean;
  externalAccountLabel: string | null;
  lastRefreshedAt: string | null;
  lastSyncedAt: string | null;
}

interface Category {
  label: string;
  providers: string[];
}

// ---------------------------------------------------------------------------
// Catalogue constants (client-side mirror for grouping only)
// ---------------------------------------------------------------------------

const CATEGORIES: Category[] = [
  { label: 'Accounting', providers: ['quickbooks', 'xero'] },
  { label: 'Calendar', providers: ['google_calendar', 'm365_calendar', 'ical_feed'] },
  { label: 'External services', providers: ['microchip', 'ip_camera', 'gbp', 'zapier'] },
  { label: 'Communications', providers: ['whatsapp'] },
];

// Providers that have a dedicated /settings/integrations/:provider sub-page.
// OAuth providers (quickbooks, xero, google_calendar, m365_calendar) manage
// state entirely via the OAuth flow — there is no extra configuration page.
const PROVIDERS_WITH_SETTINGS_PAGE = new Set(['microchip', 'ip_camera', 'gbp', 'zapier']);

// Lucide icon per provider (fallback → generic link icon)
const ICONS: Record<string, typeof LinkIcon> = {
  quickbooks: Receipt,
  xero: Receipt,
  google_calendar: Calendar,
  m365_calendar: CalendarDays,
  ical_feed: BookOpen,
  microchip: ScanBarcode,
  ip_camera: Camera,
  gbp: Star,
  zapier: Zap,
  whatsapp: MessageCircle,
};

// Brand accent colour per provider (used for the icon badge background)
const BRAND_COLOURS: Record<string, string> = {
  quickbooks: '#2CA01C',
  xero: '#13B5EA',
  google_calendar: '#4285F4',
  m365_calendar: '#0078D4',
  ical_feed: '#6B7280',
  microchip: '#6366F1',
  ip_camera: '#374151',
  gbp: '#EA4335',
  zapier: '#FF4A00',
  whatsapp: '#25D366',
};

// Human label for tier requirement
function tierLabel(tier: IntegrationRow['tier']): string {
  if (tier === 'all') return 'All tiers';
  if (tier === 'pro_or_higher') return 'Pro+';
  return 'Enterprise';
}

// Provider display names for toasts — falls back to slug if rows not yet loaded
function providerDisplayName(rows: IntegrationRow[], provider: string): string {
  return rows.find((r) => r.provider === provider)?.name ?? provider;
}

// ---------------------------------------------------------------------------
// StatusPill
// ---------------------------------------------------------------------------

type PillTone = 'success' | 'warn' | 'error' | 'muted' | 'info';

function StatusPill({ tone, children }: { tone: PillTone; children: React.ReactNode }) {
  const classes: Record<PillTone, string> = {
    success: 'bg-emerald-100 text-emerald-800',
    warn: 'bg-amber-100 text-amber-800',
    error: 'bg-red-100 text-red-800',
    muted: 'bg-secondary text-muted-foreground',
    info: 'bg-blue-100 text-blue-800',
  };
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${classes[tone]}`}
    >
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// IntegrationTile
// ---------------------------------------------------------------------------

interface TileProps {
  row: IntegrationRow;
  isBusy: boolean;
  onConnect: (row: IntegrationRow) => void;
  onDisconnect: (row: IntegrationRow) => void;
}

function IntegrationTile({ row, isBusy, onConnect, onDisconnect }: TileProps) {
  const Icon = ICONS[row.provider] ?? LinkIcon;
  const brandColor = BRAND_COLOURS[row.provider] ?? '#6B7280';

  // Status pill
  let pill: React.ReactNode = null;
  if (row.connected) {
    pill = (
      <StatusPill tone="success">
        <CheckCircle2 className="size-3" strokeWidth={2} />
        Connected
      </StatusPill>
    );
  } else if (row.status === 'expired') {
    pill = (
      <StatusPill tone="warn">
        <AlertCircle className="size-3" strokeWidth={2} />
        Reconnect required
      </StatusPill>
    );
  } else if (row.status === 'revoked') {
    pill = (
      <StatusPill tone="error">
        <AlertCircle className="size-3" strokeWidth={2} />
        Revoked
      </StatusPill>
    );
  } else if (!row.tierAllowed) {
    pill = (
      <StatusPill tone="muted">
        <Lock className="size-3" strokeWidth={2} />
        {tierLabel(row.tier)} required
      </StatusPill>
    );
  } else if (!row.available) {
    pill = (
      <StatusPill tone="warn">
        <AlertCircle className="size-3" strokeWidth={2} />
        Setup required
      </StatusPill>
    );
  } else if (row.credentialShape === 'external') {
    pill = <StatusPill tone="muted">See SMS provider</StatusPill>;
  } else {
    pill = <StatusPill tone="muted">Not connected</StatusPill>;
  }

  // Action button(s)
  let actions: React.ReactNode = null;
  if (row.connected) {
    actions = (
      <div className="flex items-center gap-2">
        {PROVIDERS_WITH_SETTINGS_PAGE.has(row.provider) ? (
          <Link
            to={`/settings/integrations/${row.provider}`}
            className="text-sm rounded-md border border-border px-3 py-1.5 hover:bg-secondary transition-colors"
          >
            Settings
          </Link>
        ) : null}
        <button
          type="button"
          className="text-sm rounded-md border border-border px-3 py-1.5 hover:bg-destructive/10 hover:border-destructive/40 hover:text-destructive transition-colors disabled:opacity-50"
          onClick={() => onDisconnect(row)}
          disabled={isBusy}
        >
          {isBusy ? <Loader2 className="size-3.5 animate-spin" /> : 'Disconnect'}
        </button>
      </div>
    );
  } else if (row.status === 'expired' || row.status === 'revoked') {
    actions = (
      <button
        type="button"
        className="text-sm rounded-md bg-primary text-primary-foreground px-3 py-1.5 hover:bg-primary/90 transition-colors disabled:opacity-50"
        onClick={() => onConnect(row)}
        disabled={isBusy}
      >
        {isBusy ? (
          <span className="flex items-center gap-1.5">
            <Loader2 className="size-3.5 animate-spin" />
            Connecting…
          </span>
        ) : (
          'Reconnect'
        )}
      </button>
    );
  } else if (!row.tierAllowed) {
    actions = (
      <Link
        to="/settings/billing"
        className="text-sm rounded-md border border-primary/50 text-primary px-3 py-1.5 hover:bg-primary/5 transition-colors"
      >
        Upgrade to {tierLabel(row.tier)}
      </Link>
    );
  } else if (row.credentialShape === 'external' && row.externalSettingsPath) {
    actions = (
      <Link
        to={row.externalSettingsPath}
        className="text-sm rounded-md bg-primary text-primary-foreground px-3 py-1.5 hover:bg-primary/90 transition-colors"
      >
        Configure
      </Link>
    );
  } else if (!row.available) {
    // OAuth is implemented but the operator hasn't set the platform
    // client-id/secret for this provider yet.
    actions = (
      <span
        className="text-xs text-muted-foreground italic"
        title="Contact the platform administrator to configure OAuth credentials for this integration"
      >
        Admin action required
      </span>
    );
  } else {
    actions = (
      <button
        type="button"
        className="text-sm rounded-md bg-primary text-primary-foreground px-3 py-1.5 hover:bg-primary/90 transition-colors disabled:opacity-50"
        onClick={() => onConnect(row)}
        disabled={isBusy}
      >
        {isBusy ? (
          <span className="flex items-center gap-1.5">
            <Loader2 className="size-3.5 animate-spin" />
            Connecting…
          </span>
        ) : (
          'Connect'
        )}
      </button>
    );
  }

  return (
    <div
      className={`rounded-xl border border-border bg-card flex flex-col overflow-hidden transition-shadow hover:shadow-sm ${
        !row.tierAllowed || (!row.available && !row.connected) ? 'opacity-70' : ''
      }`}
    >
      {/* Icon badge */}
      <div className="flex items-center justify-center p-5 pb-4">
        <div
          className="size-12 rounded-xl flex items-center justify-center text-white"
          style={{ backgroundColor: brandColor }}
          aria-hidden="true"
        >
          <Icon className="size-6" strokeWidth={1.75} />
        </div>
      </div>

      {/* Body */}
      <div className="px-4 pb-2 flex-1">
        <h3 className="text-sm font-semibold leading-tight">{row.name}</h3>
        <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{row.description}</p>
        {row.externalAccountLabel && row.connected ? (
          <p className="text-xs text-muted-foreground mt-1">
            As: <span className="font-medium">{row.externalAccountLabel}</span>
          </p>
        ) : null}
        {row.lastSyncedAt && row.connected ? (
          <p className="text-xs text-muted-foreground mt-0.5">
            Synced:{' '}
            <span className="num">{new Date(row.lastSyncedAt).toLocaleDateString('en-GB')}</span>
          </p>
        ) : null}
      </div>

      {/* Footer */}
      <div className="px-4 pt-2 pb-4 flex items-center justify-between gap-2 border-t border-border/50 mt-3">
        <div>{pill}</div>
        <div>{actions}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DisconnectModal
// ---------------------------------------------------------------------------

interface DisconnectModalProps {
  row: IntegrationRow | null;
  onConfirm: () => void;
  onCancel: () => void;
}

function DisconnectModal({ row, onConfirm, onCancel }: DisconnectModalProps) {
  if (!row) return null;

  return (
    <Modal onClose={onCancel} maxWidth="max-w-sm">
      <div className="p-6 space-y-4">
        <h2 className="text-base font-semibold">Disconnect {row.name}?</h2>
        <p className="text-sm text-muted-foreground">
          Sync will stop immediately. You can reconnect at any time.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="text-sm rounded-md border border-border px-4 py-2 hover:bg-secondary transition-colors"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="text-sm rounded-md bg-destructive text-white px-4 py-2 hover:bg-destructive/90 transition-colors"
            onClick={onConfirm}
          >
            Disconnect
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// IntegrationsPage
// ---------------------------------------------------------------------------

export function IntegrationsPage() {
  const [rows, setRows] = useState<IntegrationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyProvider, setBusyProvider] = useState<string | null>(null);
  const [disconnectTarget, setDisconnectTarget] = useState<IntegrationRow | null>(null);
  const [errorBanner, setErrorBanner] = useState<{ provider: string; reason: string } | null>(null);
  const { showToast, ToastContainer } = useToast();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/integrations', { credentials: 'include' });
      if (!res.ok) return;
      const body = (await res.json()) as { integrations: IntegrationRow[] };
      setRows(body.integrations);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle ?connected=<provider> and ?error=<provider>&reason=<reason>
  // query strings from the OAuth callback redirect. Runs once on mount —
  // the query params are consumed immediately and stripped from the URL.
  useEffect(() => {
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');
    const reason = searchParams.get('reason');

    if (connected) {
      navigate('/settings/integrations', { replace: true });
      void load().then(() => {
        showToast(`Successfully connected to ${connected.replace(/_/g, ' ')}`, 'success');
      });
      return;
    }

    if (error) {
      const reasonLabel =
        reason === 'not_implemented'
          ? 'this integration is not yet active'
          : reason === 'access_denied' || reason === 'user_cancelled_authorize'
            ? 'authorisation was cancelled'
            : reason === 'encryption_not_configured'
              ? 'the platform encryption key is not configured'
              : (reason ?? 'unknown error');
      setErrorBanner({ provider: error, reason: reasonLabel });
      navigate('/settings/integrations', { replace: true });
      void load();
      return;
    }

    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run once on mount; searchParams captured from closure

  const handleConnect = useCallback(
    async (row: IntegrationRow) => {
      if (row.credentialShape === 'external' && row.externalSettingsPath) {
        navigate(row.externalSettingsPath);
        return;
      }
      if (!row.isOauth) {
        navigate(`/settings/integrations/${row.provider}`);
        return;
      }
      setBusyProvider(row.provider);
      try {
        const res = await fetch(`/api/integrations/${row.provider}/start`, {
          credentials: 'include',
        });
        if (!res.ok) {
          showToast(`Could not start connection for ${row.name}.`, 'error');
          setBusyProvider(null);
          return;
        }
        const body = (await res.json()) as { authUrl: string };
        window.location.assign(body.authUrl);
        // Don't clear busyProvider — user is navigating away.
      } catch {
        showToast(`Could not connect to ${row.name}.`, 'error');
        setBusyProvider(null);
      }
    },
    [navigate, showToast],
  );

  const confirmDisconnect = useCallback(async () => {
    const row = disconnectTarget;
    if (!row) return;
    setDisconnectTarget(null);
    setBusyProvider(row.provider);
    try {
      const res = await fetch(`/api/integrations/${row.provider}/disconnect`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        showToast(`Could not disconnect ${row.name}.`, 'error');
        return;
      }
      showToast(`Disconnected from ${row.name}.`, 'info');
      await load();
    } finally {
      setBusyProvider(null);
    }
  }, [disconnectTarget, load, showToast]);

  const retryConnect = useCallback(() => {
    if (!errorBanner) return;
    const row = rows.find((r) => r.provider === errorBanner.provider);
    setErrorBanner(null);
    if (row) void handleConnect(row);
  }, [errorBanner, rows, handleConnect]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
        <Loader2 className="size-4 animate-spin" />
        Loading integrations…
      </div>
    );
  }

  const rowMap = new Map(rows.map((r) => [r.provider, r]));

  return (
    <div className="space-y-8 pb-12">
      <header>
        <h1 className="text-xl font-semibold">Integrations</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Connect external services to sync data, mirror bookings, and fan out events.
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          &ldquo;Setup required&rdquo; tiles are fully implemented — they need the platform
          administrator to add the provider&rsquo;s OAuth client credentials before you can connect.
        </p>
      </header>

      {errorBanner ? (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm flex items-start gap-3">
          <AlertCircle className="size-4 text-amber-600 mt-0.5 shrink-0" strokeWidth={1.75} />
          <div className="flex-1">
            <p className="font-medium text-amber-900">
              Could not connect to {providerDisplayName(rows, errorBanner.provider)}.
            </p>
            <p className="text-amber-800 mt-0.5">{errorBanner.reason}</p>
          </div>
          <div className="flex gap-2 shrink-0 mt-0.5">
            {rowMap.get(errorBanner.provider)?.isOauth &&
            rowMap.get(errorBanner.provider)?.tierAllowed &&
            rowMap.get(errorBanner.provider)?.available ? (
              <button
                type="button"
                className="text-xs font-medium text-amber-900 underline underline-offset-2"
                onClick={retryConnect}
              >
                Retry
              </button>
            ) : null}
            <button
              type="button"
              className="text-xs font-medium text-amber-700 underline underline-offset-2"
              onClick={() => setErrorBanner(null)}
            >
              Dismiss
            </button>
          </div>
        </div>
      ) : null}

      {CATEGORIES.map((cat) => {
        const catRows = cat.providers.map((p) => rowMap.get(p)).filter(Boolean) as IntegrationRow[];
        if (catRows.length === 0) return null;
        return (
          <section key={cat.label} className="space-y-3">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              {cat.label}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {catRows.map((row) => (
                <IntegrationTile
                  key={row.provider}
                  row={row}
                  isBusy={busyProvider === row.provider}
                  onConnect={handleConnect}
                  onDisconnect={setDisconnectTarget}
                />
              ))}
            </div>
          </section>
        );
      })}

      <footer className="text-xs text-muted-foreground pt-2 border-t border-border">
        <p>
          Need outbound webhooks (Zapier-style)?{' '}
          <Link to="/settings/integrations/zapier" className="underline">
            Configure webhook subscribers
          </Link>
          .
        </p>
      </footer>

      <DisconnectModal
        row={disconnectTarget}
        onConfirm={() => void confirmDisconnect()}
        onCancel={() => setDisconnectTarget(null)}
      />

      <ToastContainer />
    </div>
  );
}
